import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  currentMedia, getSnapshot, goLive, openCamera, stop, subscribe,
  type BroadcastSnapshot,
} from './broadcastSession'

/**
 * React's window onto the module-level broadcast session.
 *
 * There is no state here and that is the whole design: the session outlives
 * every component that shows it, so a panel unmounting cannot end a broadcast.
 * `useSyncExternalStore` is the right primitive — it gives a tearing-free read
 * of an external store and needs no effect to subscribe.
 */
export function useBroadcastSession(): BroadcastSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Seconds on air, ticking. Derived from `startedAt` so it survives navigation. */
export function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (startedAt == null) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [startedAt])
  return startedAt == null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
}

/**
 * Point a `<video>` at whatever the session is capturing.
 *
 * Re-attaches whenever the element or the stream changes — which is every time a
 * panel remounts after navigation, and is exactly why the preview comes back
 * instead of showing a black rectangle over a broadcast that never stopped.
 */
export function useAttachPreview(
  el: HTMLVideoElement | null,
  phase: BroadcastSnapshot['phase'],
) {
  useEffect(() => {
    if (!el) return
    const stream = currentMedia()
    el.srcObject = stream
    return () => { el.srcObject = null }
  }, [el, phase])
}

export { openCamera, goLive, stop }
